<script setup lang="ts">
import { debounce } from "lodash-es";
import { computed, shallowRef, toRefs, watch } from "vue";
import CtyunPlayer from "./ctyun-player.vue";
import EasyPlayer from "./easyPlayer.vue";
import {
  getVideoType,
  isSupportedProtocol,
  TzjPlayerProps,
} from "./tzj-player";
import YsPlayer from "./ysPlayer.vue";
import YunzhiyanPlayer from "./yunzhiyanPlayer.vue";

defineOptions({
  name: "ITzjPlayer",
});

const props = defineProps(TzjPlayerProps);
const { url, platformTypeName, accessToken } = toRefs(props);

const compRef = shallowRef();

// 防抖处理组件渲染更新，避免频繁切换播放器类型
const updatePlayerComponent = debounce(() => {
  // 强制更新组件，销毁旧实例
  if (compRef.value) {
    compRef.value.destroy?.();
  }
}, 300);

// 监听URL变化，使用防抖处理
watch(
  () => [url.value, platformTypeName.value, accessToken.value],
  ([newUrl, newPlatform, newToken], [oldUrl, oldPlatform, oldToken]) => {
    // 只有当值真正发生变化时才更新
    if (
      newUrl !== oldUrl ||
      newPlatform !== oldPlatform ||
      newToken !== oldToken
    ) {
      // 更新播放器组件
      updatePlayerComponent();
    }
  },
);

/**
 * 平台类型映射表，提升可维护性
 */
const PLATFORM_COMPONENTS = {
  云智眼: YunzhiyanPlayer,
  萤石云: YsPlayer,
  天翼云: CtyunPlayer,
  // hls: XgPlayer,
} as const;

/**
 * 计算当前播放器组件类型
 * 使用计算属性避免重复计算，提升性能
 */
const playerComponent = computed(() => {
  // 优先使用指定的平台类型
  if (platformTypeName.value) {
    return (
      PLATFORM_COMPONENTS[
        platformTypeName.value as keyof typeof PLATFORM_COMPONENTS
      ] || EasyPlayer
    );
  }

  // 未指定平台类型时，根据URL自动判断
  if (!url.value) {
    return null;
  }

  // 检查协议支持性
  if (!isSupportedProtocol(url.value)) {
    return null;
  }

  // ezopen协议使用萤石云播放器
  if (url.value.includes("ezopen")) {
    return YsPlayer;
  }

  // 根据视频类型选择播放器
  const videoType = getVideoType(url.value);
  return videoType ? EasyPlayer : null;
});

/**
 * 计算错误状态信息
 * 统一错误状态管理，提升用户体验
 */
const errorState = computed(() => {
  // 云智眼平台不需要URL
  if (
    platformTypeName.value === "云智眼" ||
    platformTypeName.value === "天翼云"
  ) {
    return null;
  }

  // 检查URL是否存在
  if (!url.value) {
    return {
      type: "no-url",
      message: "无视频地址",
    };
  }
  // 检查协议支持性
  if (!isSupportedProtocol(url.value)) {
    return {
      type: "unsupported-protocol",
      message: "不支持该视频协议",
    };
  }

  // 萤石云平台需要accessToken
  if (
    (platformTypeName.value === "萤石云" || url.value.includes("ezopen")) &&
    !accessToken.value
  ) {
    return {
      type: "no-token",
      message: "缺少访问令牌",
    };
  }

  return null;
});

/**
 * 暴露的方法，增加错误处理
 */
defineExpose({
  /**
   * 播放视频
   * @param options 播放选项
   */
  play(options?: any) {
    if (!compRef.value) {
      props.debug && console.warn("[TzjPlayer] 播放器组件未初始化");
      return;
    }
    compRef.value?.play(options);
  },

  /**
   * 销毁播放器
   */
  destroy() {
    if (!compRef.value) {
      props.debug && console.warn("[TzjPlayer] 播放器组件未初始化");
      return;
    }
    compRef.value?.destroy();
  },

  /**
   * 调整播放器尺寸
   */
  resize() {
    if (!compRef.value) {
      props.debug && console.warn("[TzjPlayer] 播放器组件未初始化");
      return;
    }
    compRef.value?.resize();
  },

  /**
   * 获取当前播放器类型
   */
  getPlayerType() {
    return playerComponent.value?.name || null;
  },

  /**
   * 获取错误状态
   */
  getErrorState() {
    return errorState.value;
  },
});
</script>

<template>
  <div class="i-tzj-player">
    <!-- 头部标题栏 -->
    <div class="i-tzj-player__header">
      <i class="iconfont icon-shexiangtou4" />
      <span>{{ areaName || deviceSerial || "摄像头" }}</span>
    </div>

    <!-- 播放器组件 -->
    <component :is="playerComponent" ref="compRef" v-bind="props" />
  </div>
</template>
